import {
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
} from '@chakra-ui/react';
import { Select as ChakraSelect, type SingleValue } from 'chakra-react-select';
import { type FormEvent, useState } from 'react';

import { Button } from '@revelio/ui';

import { FilterId } from '../../../filters/types/filters';
import modalStyles from '../../Modal/themedModal.module.css';
import type { ReportParameterConfig } from '../../PageConfigProvider/page-reducer';
import type { PlotParameter } from '../../PageConfigProvider/types/item';
import {
  defaultReportParameterFilters,
  filterCanBePrimary,
  toReadableFilterName,
} from './parameters';

export function EditParameterModal({
  isOpen,
  onClose,
  mode,
  initial,
  onSubmit,
  isChartLevel = false,
  plotParameter,
  onGroupingChange,
}: {
  isOpen: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  initial: ReportParameterConfig;
  onSubmit: (param: ReportParameterConfig) => void;
  isChartLevel?: boolean;
  plotParameter?: PlotParameter;
  onGroupingChange?: (grouped: boolean) => void;
}) {
  const [draft, setDraft] = useState<ReportParameterConfig>({
    ...initial,
    selectionLimit: initial.selectionLimit ?? 1,
  });

  const [groupingDraft, setGroupingDraft] = useState<boolean>(
    plotParameter?.grouped ?? true
  );

  const availableFilterIds = defaultReportParameterFilters;

  type FilterOption = {
    value: FilterId;
    label: string;
    isDisabled?: boolean;
  };

  const save = () => {
    onSubmit(draft);
    if (isChartLevel && onGroupingChange) {
      onGroupingChange(groupingDraft);
    }
    onClose();
  };

  const isDateRange = draft.filterId === FilterId.DATE_RANGE;

  const showGroupingUI =
    isChartLevel &&
    !initial.isPrimary &&
    !isDateRange &&
    (draft.selectionLimit ?? 1) > 1;

  const unitOfAnalysisOptions = availableFilterIds.filter(filterCanBePrimary);

  const options: FilterOption[] = [
    ...unitOfAnalysisOptions.map((id) => ({
      value: id,
      label: id === FilterId.COMPANY ? 'Company' : toReadableFilterName(id),
    })),
    {
      value: FilterId.DATE_RANGE,
      label: toReadableFilterName(FilterId.DATE_RANGE),
    },
  ];

  const selected: FilterOption | null =
    options.find((opt) => opt.value === draft.filterId) ?? null;

  const handleSelectChange = (opt: SingleValue<FilterOption>) => {
    if (!opt) return;
    const nextFilterId = opt.value;
    setDraft((prev) => {
      const prevDefault = toReadableFilterName(prev.filterId);
      const nextDefault =
        nextFilterId === FilterId.COMPANY
          ? 'Company'
          : toReadableFilterName(nextFilterId);
      const shouldUpdateName = !prev.name || prev.name === prevDefault;
      const nextIsDateRange = nextFilterId === FilterId.DATE_RANGE;
      return {
        ...prev,
        filterId: nextFilterId,
        name: shouldUpdateName ? nextDefault : prev.name,
        ...(nextIsDateRange
          ? { isPrimary: false, isOptional: true, selectionLimit: undefined }
          : { isOptional: false, selectionLimit: prev.selectionLimit ?? 1 }),
      };
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent
        className={modalStyles.content}
        as="form"
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          save();
        }}
      >
        <ModalHeader color="text.primary">
          {mode === 'add' ? 'Add Parameter' : 'Edit Parameter'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb={3}>
            <FormLabel>Parameters</FormLabel>
            <div data-testid="parameter-select">
              <ChakraSelect<FilterOption, false>
                options={options}
                value={selected}
                onChange={handleSelectChange}
                isSearchable
              />
            </div>
          </FormControl>
          <FormControl mb={3}>
            <FormLabel>Name</FormLabel>
            <Input
              value={draft.name}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  name: e.target.value,
                })
              }
            />
          </FormControl>
          {!isDateRange && (
            <FormControl mb={3}>
              <FormLabel>Selection Limit</FormLabel>
              <NumberInput
                value={draft.selectionLimit ?? 1}
                min={1}
                onChange={(_, valueAsNumber) =>
                  setDraft({
                    ...draft,
                    // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
                    selectionLimit: isNaN(valueAsNumber) ? 1 : valueAsNumber,
                  })
                }
              >
                <NumberInputField />
              </NumberInput>
            </FormControl>
          )}
          {showGroupingUI && (
            <FormControl mb={3}>
              <Checkbox
                isChecked={groupingDraft}
                onChange={(e) => setGroupingDraft(e.target.checked)}
              >
                Group Parameter
              </Checkbox>
            </FormControl>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            size="sm"
            color="secondary"
            variant="ghost"
            style={{ marginRight: '0.5rem' }}
            type="button"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button size="sm" color="primary" type="submit">
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
